import { useState } from "react";
import {
  CalendarDays,
  List,
  LayoutGrid,
  Rows3,
  ChevronLeft,
  ChevronRight,
  Wrench,
  CalendarClock,
  SlidersHorizontal,
  Search,
  X,
  Users,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
} from "@/components/ui/responsive-dialog";
import { StatusDot } from "@/components/StatusPill";
import { monthTitle, KINDS, STATUSES, STATUS_LABEL_NN } from "@/lib/calendar";
import { cn } from "@/lib/utils";

const VIEW_TABS = [
  { value: "month", label: "Månad", icon: CalendarDays },
  { value: "list", label: "Liste", icon: List },
  { value: "year", label: "År", icon: LayoutGrid },
  { value: "machines", label: "Maskinar", shortLabel: "Maskin", icon: Rows3 },
];

const chipCls = (active, muted) =>
  cn(
    "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60",
    active
      ? "border-transparent bg-ink text-white dark:bg-white dark:text-ink"
      : muted
        ? "bg-card text-muted-foreground/70 hover:bg-accent hover:text-foreground"
        : "bg-card text-foreground hover:bg-accent"
  );

/**
 * Filter-chips med «smart» oppførsel:
 *  - alle valt (= ingen filter) -> nøytrale chips
 *  - klikk på ein chip når alle er valt -> filtrer til berre den
 *  - klikk på siste aktive -> tilbake til alle
 */
const FilterChips = ({ options, value, onChange, testid, testidFor, label }) => {
  const all = options.map((o) => o.value);
  const isAll = value.length === all.length;
  const toggle = (v) => {
    if (isAll) return onChange([v]);
    if (value.includes(v)) {
      const next = value.filter((x) => x !== v);
      return onChange(next.length ? next : all);
    }
    const next = [...value, v];
    return onChange(next.length === all.length ? all : next);
  };
  return (
    <div role="group" aria-label={label} className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map((o) => {
        const active = !isAll && value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            data-state={active ? "on" : "off"}
            data-testid={testidFor ? testidFor(o.value) : `${testid}-${o.value}`}
            onClick={() => toggle(o.value)}
            className={chipCls(active, !isAll && !active)}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

export const Legend = ({ className }) => (
  <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground", className)} data-testid="calendar-legend">
    <span className="flex items-center gap-1.5"><StatusDot status="overdue" /> Forfalt</span>
    <span className="flex items-center gap-1.5"><StatusDot status="upcoming" /> Nærmar seg</span>
    <span className="flex items-center gap-1.5"><StatusDot status="ok" /> OK</span>
    <span className="flex items-center gap-1.5"><span className="cal-fill-planned inline-block h-2.5 w-2.5 rounded-full" /> Planlagt</span>
  </div>
);

export const CalendarToolbar = ({
  view,
  onView,
  anchor,
  onPrev,
  onNext,
  onToday,
  kinds,
  onKinds,
  statuses,
  onStatuses,
  customer,
  onCustomer,
  q,
  onQ,
  customers,
  group,
  onGroup,
  activeFilters,
  onReset,
  summary,
  onRefresh,
  refreshing,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const showPeriod = view !== "list";
  const periodLabel = view === "month" ? monthTitle(anchor) : String(anchor.getFullYear());

  // Plain function (not a component) so inputs keep focus between re-renders
  const renderFilterFields = (inSheet = false) => (
    <>
      <FilterChips
        label="Status"
        testid="calendar-filter-status"
        options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL_NN[s], icon: <StatusDot status={s} className="h-2 w-2" /> }))}
        value={statuses}
        onChange={onStatuses}
      />

      <Select value={customer || "all"} onValueChange={(v) => onCustomer(v === "all" ? "" : v)}>
        <SelectTrigger className={cn("h-9 rounded-full bg-card text-xs font-semibold", inSheet ? "w-full" : "w-[200px]")} data-testid="calendar-filter-customer">
          <Users className="mr-1.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <SelectValue placeholder="Alle kundar" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Alle kundar</SelectItem>
          {customers.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className={cn("relative", inSheet ? "w-full" : "w-[200px]")}>
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => onQ(e.target.value)}
          placeholder="Søk maskin, type …"
          className="h-9 rounded-full bg-card pl-8 pr-8 text-xs"
          data-testid="calendar-filter-search"
        />
        {q && (
          <button type="button" onClick={() => onQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted" aria-label="Tøm søk">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </>
  );

  return (
    <div className="space-y-2.5" data-testid="calendar-toolbar">
      {/* Row 1: period nav / group toggle (left) + view tabs + refresh (right) */}
      <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
        {showPeriod ? (
          <div className="flex items-center gap-1.5" data-testid="calendar-period-nav">
            <Button variant="outline" size="icon" className="h-10 w-10 rounded-lg" onClick={onPrev} aria-label="Førre periode" data-testid="calendar-prev">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <button
              type="button"
              onClick={onToday}
              className="min-w-[160px] rounded-lg px-2 py-1 text-center font-heading text-xl font-extrabold tracking-tight transition-colors hover:bg-accent sm:min-w-[200px] sm:text-2xl"
              data-testid="calendar-period-label"
              title="Gå til i dag"
            >
              {periodLabel}
            </button>
            <Button variant="outline" size="icon" className="h-10 w-10 rounded-lg" onClick={onNext} aria-label="Neste periode" data-testid="calendar-next">
              <ChevronRight className="h-5 w-5" />
            </Button>
            <Button variant="ghost" className="h-10 rounded-lg px-3 text-sm font-semibold" onClick={onToday} data-testid="calendar-today">
              I dag
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-heading text-xl font-extrabold tracking-tight sm:text-2xl">Fristar</h2>
            <ToggleGroup
              type="single"
              value={group}
              onValueChange={(v) => v && onGroup(v)}
              className="inline-flex rounded-lg border bg-card p-0.5"
              data-testid="calendar-group-toggle"
              aria-label="Grupper etter"
            >
              <ToggleGroupItem value="date" className="h-8 rounded-md px-3 text-xs font-semibold data-[state=on]:bg-ink data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-ink" data-testid="calendar-group-date">
                Etter dato
              </ToggleGroupItem>
              <ToggleGroupItem value="customer" className="h-8 rounded-md px-3 text-xs font-semibold data-[state=on]:bg-ink data-[state=on]:text-white dark:data-[state=on]:bg-white dark:data-[state=on]:text-ink" data-testid="calendar-group-customer">
                Etter kunde
              </ToggleGroupItem>
            </ToggleGroup>
            {summary && (
              <div className="hidden items-center gap-3 text-xs text-muted-foreground sm:flex" data-testid="calendar-summary">
                <span><b className="tabular-nums text-foreground">{summary.total}</b> fristar</span>
                {summary.overdue > 0 && <span className="flex items-center gap-1"><StatusDot status="overdue" /> <b className="tabular-nums text-foreground">{summary.overdue}</b></span>}
                {summary.upcoming > 0 && <span className="flex items-center gap-1"><StatusDot status="upcoming" /> <b className="tabular-nums text-foreground">{summary.upcoming}</b></span>}
                {summary.planned > 0 && <span className="flex items-center gap-1"><span className="cal-fill-planned inline-block h-2.5 w-2.5 rounded-full" /> <b className="tabular-nums text-foreground">{summary.planned}</b></span>}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && onView(v)}
            className="grid flex-1 grid-cols-4 rounded-xl bg-muted p-1 lg:inline-flex lg:flex-none"
            data-testid="calendar-view-tabs"
            aria-label="Visning"
          >
            {VIEW_TABS.map(({ value, label, shortLabel, icon: Icon }) => (
              <ToggleGroupItem
                key={value}
                value={value}
                data-testid={`calendar-view-${value}`}
                className={cn(
                  "h-9 gap-1 rounded-lg px-1 text-[11px] font-semibold text-muted-foreground transition-colors sm:gap-1.5 sm:px-2 sm:text-sm lg:px-3",
                  "data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-sm",
                  "hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="truncate sm:hidden">{shortLabel || label}</span>
                <span className="hidden truncate sm:inline">{label}</span>
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          {onRefresh && (
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0 rounded-lg" onClick={onRefresh} aria-label="Oppdater" data-testid="calendar-refresh" disabled={refreshing}>
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: filters */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterChips
          label="Type"
          testid="calendar-filters"
          testidFor={(v) => `calendar-filter-${v}`}
          options={[
            { value: "service", label: "Service", icon: <Wrench className="h-3.5 w-3.5" /> },
            { value: "annual", label: "Årskontroll", icon: <CalendarClock className="h-3.5 w-3.5" /> },
          ]}
          value={kinds}
          onChange={onKinds}
        />

        <div className="hidden flex-wrap items-center gap-2 lg:flex">
          <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          {renderFilterFields(false)}
        </div>

        {/* Mobile: filter sheet */}
        <Button variant="outline" className="h-9 rounded-full lg:hidden" onClick={() => setSheetOpen(true)} data-testid="calendar-filter-button">
          <SlidersHorizontal className="h-3.5 w-3.5" /> Filter
          {activeFilters > 0 && (
            <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-ink">{activeFilters}</span>
          )}
        </Button>

        {activeFilters > 0 && (
          <Button variant="ghost" size="sm" className="h-9 rounded-full text-xs" onClick={onReset} data-testid="calendar-filter-reset">
            <X className="h-3.5 w-3.5" /> Nullstill
          </Button>
        )}

        <Legend className="ml-auto hidden lg:flex" />
      </div>

      <ResponsiveDialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <ResponsiveDialogContent className="sm:max-w-md" data-testid="calendar-filter-sheet">
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-heading">Filter</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {activeFilters > 0 ? `${activeFilters} aktive filter` : "Avgrens fristane etter status, kunde eller søk."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogBody>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status, kunde og søk</Label>
              <div className="flex flex-col gap-2.5 [&_.h-9]:h-11 [&_input]:h-11 [&_input]:text-sm [&_button[role=combobox]]:h-11 [&_button[role=combobox]]:text-sm">
                {renderFilterFields(true)}
              </div>
            </div>
            <Legend />
          </ResponsiveDialogBody>
          <ResponsiveDialogFooter className="grid grid-cols-2 gap-2 md:flex">
            <Button variant="outline" disabled={activeFilters === 0} onClick={() => { onReset(); setSheetOpen(false); }} data-testid="calendar-filter-sheet-reset">Nullstill</Button>
            <Button onClick={() => setSheetOpen(false)} data-testid="calendar-filter-sheet-done">Ferdig</Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  );
};
